import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { cariDB, type CariSummary, type CariTransaction } from '../../lib/cariDB';
import { pricingDB } from '../../lib/pricingDB';
import CariAddModal from './components/CariAddModal';
import CariDetailModal from './components/CariDetailModal';
import CariClinicModal from './components/CariClinicModal';
import ConfirmModal from '../../components/ui/ConfirmModal';

export default function CariPanel() {
  const navigate = useNavigate();
  const [summaries, setSummaries] = useState<CariSummary[]>([]);
  const [allTransactions, setAllTransactions] = useState<CariTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [successMsg, setSuccessMsg] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const [showAddModal, setShowAddModal] = useState(false);
  const [addModalClinic, setAddModalClinic] = useState('');
  const [addModalType, setAddModalType] = useState<'borc' | 'odeme'>('odeme');
  const [showClinicModal, setShowClinicModal] = useState(false);

  const [detailClinic, setDetailClinic] = useState<CariSummary | null>(null);
  const [detailTransactions, setDetailTransactions] = useState<CariTransaction[]>([]);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const pricing = pricingDB.get();

  const loadData = useCallback(async () => {
    try {
      const [sums, txs] = await Promise.all([cariDB.getSummaries(), cariDB.getAll()]);
      setSummaries(sums);
      setAllTransactions(txs);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const showSuccess = (msg: string) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(''), 3000);
  };

  const handleAddTx = async (data: Omit<CariTransaction, 'id' | 'created_at'>) => {
    await cariDB.insert(data);
    showSuccess(data.type === 'borc' ? 'Borç eklendi!' : 'Ödeme kaydedildi!');
    setShowAddModal(false);
    if (detailClinic) {
      const txs = await cariDB.getByClinic(detailClinic.clinic_name);
      setDetailTransactions(txs);
    }
    await loadData();
  };

  const handleDeleteTx = async (id: string) => {
    setConfirmDeleteId(id);
  };

  const confirmDelete = async () => {
    if (!confirmDeleteId) return;
    await cariDB.delete(confirmDeleteId);
    setConfirmDeleteId(null);
    showSuccess('İşlem silindi!');
    if (detailClinic) {
      const txs = await cariDB.getByClinic(detailClinic.clinic_name);
      setDetailTransactions(txs);
    }
    await loadData();
  };

  const openDetail = async (sum: CariSummary) => {
    const txs = await cariDB.getByClinic(sum.clinic_name);
    setDetailTransactions(txs);
    setDetailClinic(sum);
  };

  const openAddModal = (clinic: string, type: 'borc' | 'odeme') => {
    setAddModalClinic(clinic);
    setAddModalType(type);
    setShowAddModal(true);
    setDetailClinic(null);
  };

  const filteredSummaries = summaries.filter((s) =>
    s.clinic_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const totalBorc = summaries.reduce((s, c) => s + c.total_borc, 0);
  const totalOdeme = summaries.reduce((s, c) => s + c.total_odeme, 0);
  const netBakiye = totalBorc - totalOdeme;

  const fmt = (n: number) => `${pricing.currency}${n.toLocaleString('tr-TR')}`;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-cyan-50 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-teal-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-cyan-50">
      {/* Navbar */}
      <nav className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-xl flex items-center justify-center">
              <i className="ri-bank-line text-lg text-white"></i>
            </div>
            <div>
              <h1 className="text-base font-bold text-gray-900">Cari Panel</h1>
              <p className="text-xs text-gray-500">Klinik borç &amp; ödeme takibi</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowClinicModal(true)}
              className="px-4 py-2 bg-gradient-to-r from-teal-500 to-cyan-600 text-white rounded-lg font-medium hover:from-teal-600 hover:to-cyan-700 transition-all flex items-center gap-2 whitespace-nowrap cursor-pointer text-sm"
            >
              <i className="ri-add-line"></i>Cari Ekle
            </button>
            <button
              onClick={() => { setAddModalClinic(''); setAddModalType('borc'); setShowAddModal(true); }}
              className="px-4 py-2 text-teal-700 bg-teal-50 border border-teal-200 hover:bg-teal-100 rounded-lg transition-all flex items-center gap-2 whitespace-nowrap cursor-pointer text-sm font-medium"
            >
              <i className="ri-list-check-line"></i>İşlem Ekle
            </button>
            <button onClick={() => navigate('/dashboard')}
              className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-all flex items-center gap-2 whitespace-nowrap cursor-pointer text-sm">
              <i className="ri-arrow-left-line"></i>Geri
            </button>
          </div>
        </div>
      </nav>

      {successMsg && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50 px-6 py-3 bg-green-500 text-white rounded-xl flex items-center gap-2 text-sm">
          <i className="ri-check-line text-lg"></i>{successMsg}
        </div>
      )}

      <div className="max-w-6xl mx-auto px-6 py-8 space-y-6">

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { label: 'Toplam Borç', val: totalBorc, icon: 'ri-arrow-up-circle-line', iconBg: 'bg-red-100', iconColor: 'text-red-600', valColor: 'text-red-600', sub: `${summaries.length} klinik` },
            { label: 'Toplam Ödeme', val: totalOdeme, icon: 'ri-arrow-down-circle-line', iconBg: 'bg-emerald-100', iconColor: 'text-emerald-600', valColor: 'text-emerald-600', sub: `${allTransactions.filter((t) => t.type === 'odeme').length} işlem` },
            { label: 'Net Bakiye', val: netBakiye, icon: 'ri-scales-line', iconBg: netBakiye > 0 ? 'bg-red-100' : 'bg-emerald-100', iconColor: netBakiye > 0 ? 'text-red-600' : 'text-emerald-600', valColor: netBakiye > 0 ? 'text-red-600' : 'text-emerald-600', sub: netBakiye > 0 ? 'Alacaklısınız' : netBakiye < 0 ? 'Fazla ödeme var' : 'Eşit' },
          ].map((card) => (
            <div key={card.label} className="bg-white rounded-2xl border border-gray-100 p-5 flex items-center gap-4">
              <div className={`w-14 h-14 flex items-center justify-center rounded-xl ${card.iconBg} flex-shrink-0`}>
                <i className={`${card.icon} text-2xl ${card.iconColor}`}></i>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-0.5">{card.label}</p>
                <p className={`text-2xl font-bold ${card.valColor}`}>{fmt(card.val)}</p>
                <p className="text-xs text-gray-400">{card.sub}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Search */}
        <div className="flex gap-3">
          <div className="relative flex-1">
            <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"></i>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Klinik ara..."
              className="w-full pl-11 pr-4 py-3 bg-white border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Clinic List */}
        {filteredSummaries.length === 0 ? (
          <div className="bg-white rounded-2xl border border-dashed border-gray-200 p-12 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-bank-line text-3xl text-gray-400"></i>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {searchQuery ? 'Klinik bulunamadı' : 'Henüz cari hesap yok'}
            </h3>
            <p className="text-sm text-gray-500 mb-6">
              {searchQuery ? 'Arama kriterini değiştirin' : '"Cari Ekle" ile yeni klinik kaydı oluşturun'}
            </p>
            {!searchQuery && (
              <button
                onClick={() => setShowClinicModal(true)}
                className="px-5 py-2.5 bg-teal-500 text-white rounded-lg text-sm font-medium hover:bg-teal-600 transition-all cursor-pointer whitespace-nowrap flex items-center gap-2 mx-auto"
              >
                <i className="ri-add-line"></i>Cari Ekle
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {filteredSummaries.map((sum) => {
              const paymentRatio = sum.total_borc > 0 ? (sum.total_odeme / sum.total_borc) * 100 : 0;
              const isSettled = sum.bakiye === 0 && sum.total_borc > 0;
              const isOverpaid = sum.bakiye < 0;

              return (
                <div
                  key={sum.clinic_name}
                  className="bg-white rounded-2xl border border-gray-100 overflow-hidden hover:border-teal-200 transition-all cursor-pointer"
                  onClick={() => openDetail(sum)}
                >
                  <div className="p-5">
                    <div className="flex items-center justify-between gap-4">
                      {/* Left */}
                      <div className="flex items-center gap-4 flex-1 min-w-0">
                        <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-xl flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                          {sum.clinic_name.slice(0, 1).toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="text-base font-bold text-gray-900 truncate">{sum.clinic_name}</h3>
                            {isSettled && (
                              <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-bold whitespace-nowrap">
                                <i className="ri-check-line mr-0.5"></i>Kapatıldı
                              </span>
                            )}
                            {isOverpaid && (
                              <span className="px-2 py-0.5 bg-cyan-100 text-cyan-700 rounded-full text-xs font-bold whitespace-nowrap">Fazla Ödeme</span>
                            )}
                          </div>
                          <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                            {sum.contact_name && (
                              <span className="text-xs text-gray-500 flex items-center gap-1">
                                <i className="ri-user-line text-gray-400"></i>{sum.contact_name}
                              </span>
                            )}
                            {sum.phone && (
                              <span className="text-xs text-gray-500 flex items-center gap-1">
                                <i className="ri-phone-line text-gray-400"></i>{sum.phone}
                              </span>
                            )}
                            {!sum.contact_name && !sum.phone && (
                              <span className="text-xs text-gray-400">
                                Son işlem: {sum.last_transaction ? new Date(sum.last_transaction).toLocaleDateString('tr-TR') : '—'}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Right */}
                      <div className="flex items-center gap-6 flex-shrink-0">
                        <div className="text-right hidden sm:block">
                          <p className="text-xs text-gray-400">Borç</p>
                          <p className="text-sm font-bold text-red-600">{fmt(sum.total_borc)}</p>
                        </div>
                        <div className="text-right hidden sm:block">
                          <p className="text-xs text-gray-400">Ödeme</p>
                          <p className="text-sm font-bold text-emerald-600">{fmt(sum.total_odeme)}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-gray-400">Bakiye</p>
                          <p className={`text-lg font-bold ${sum.bakiye > 0 ? 'text-red-600' : sum.bakiye < 0 ? 'text-emerald-600' : 'text-gray-500'}`}>
                            {sum.bakiye > 0 ? '+' : ''}{fmt(sum.bakiye)}
                          </p>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <button
                            onClick={(e) => { e.stopPropagation(); openAddModal(sum.clinic_name, 'odeme'); }}
                            className="px-3 py-1.5 bg-emerald-50 text-emerald-600 border border-emerald-200 rounded-lg text-xs font-semibold hover:bg-emerald-100 transition-all cursor-pointer whitespace-nowrap flex items-center gap-1"
                          >
                            <i className="ri-arrow-down-circle-line"></i>Ödeme
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); openAddModal(sum.clinic_name, 'borc'); }}
                            className="px-3 py-1.5 bg-red-50 text-red-600 border border-red-200 rounded-lg text-xs font-semibold hover:bg-red-100 transition-all cursor-pointer whitespace-nowrap flex items-center gap-1"
                          >
                            <i className="ri-arrow-up-circle-line"></i>Borç
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Progress Bar */}
                    {sum.total_borc > 0 && (
                      <div className="mt-4">
                        <div className="flex justify-between text-xs text-gray-400 mb-1.5">
                          <span>Ödeme oranı</span>
                          <span className="font-semibold">{Math.min(100, Math.round(paymentRatio))}%</span>
                        </div>
                        <div className="w-full bg-gray-100 rounded-full h-2">
                          <div
                            className={`h-2 rounded-full transition-all ${isSettled || isOverpaid ? 'bg-emerald-500' : 'bg-teal-500'}`}
                            style={{ width: `${Math.min(100, paymentRatio)}%` }}
                          ></div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Clinic Add Modal */}
      {showClinicModal && (
        <CariClinicModal
          onSaved={() => {
            setShowClinicModal(false);
            showSuccess('Klinik eklendi!');
            loadData();
          }}
          onClose={() => setShowClinicModal(false)}
        />
      )}

      {/* Transaction Add Modal */}
      {showAddModal && (
        <CariAddModal
          clinicName={addModalClinic}
          prefilledType={addModalType}
          onSave={handleAddTx}
          onClose={() => setShowAddModal(false)}
        />
      )}

      {/* Detail Modal */}
      {detailClinic && (
        <CariDetailModal
          summary={detailClinic}
          transactions={detailTransactions}
          currency={pricing.currency}
          onClose={() => setDetailClinic(null)}
          onAddPayment={() => { setDetailClinic(null); openAddModal(detailClinic.clinic_name, 'odeme'); }}
          onAddDebt={() => { setDetailClinic(null); openAddModal(detailClinic.clinic_name, 'borc'); }}
          onDelete={handleDeleteTx}
          onUpdateClinic={async (contactName, phone) => {
            const clinic = await cariDB.getClinicByName(detailClinic.clinic_name);
            if (clinic) {
              await cariDB.updateClinic(clinic.id, { contact_name: contactName, phone });
            }
            showSuccess('Bilgiler güncellendi!');
            await loadData();
            setDetailClinic((prev) => prev ? { ...prev, contact_name: contactName, phone } : prev);
          }}
        />
      )}

      {confirmDeleteId && (
        <ConfirmModal
          title="İşlemi Sil"
          message="Bu cari işlemi silmek istediğinizden emin misiniz? Bu işlem geri alınamaz."
          confirmLabel="Sil"
          onConfirm={confirmDelete}
          onCancel={() => setConfirmDeleteId(null)}
        />
      )}
    </div>
  );
}
